import type { FC } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiGallection } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  BiData,
  BiContextProvidedValue,
  ClickCopyBiEvent,
  DownloadAllClickBiEvent,
} from '@wix/da-bi/pkg/events';
import isSSR from '@wix/da-shared-react/pkg/utils/isSSR';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import ShareIcon from '@wix/da-ds/pkg/Icons/24x24/Share';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import FolderDescription from '../../FolderDescription';
import StudioBreadcrumbs from '../../StudioBreadcrumbs';
import FolderActionsDropdown from '../../_partials/FolderActionsDropdown';
import cn from 'classnames';
import SimpleUploadButton from '../../SimpleUploadButton';
import { useSelector } from 'react-redux';
import { getActiveSection } from '../../../selectors/studio';
import { StudioSection } from '../../../types/store';

import s from './FolderHeaderMobile.scss';

interface Props {
  folder: PapiGallection;
  isOwner?: boolean;
  onShareClick: () => void;
}

const FolderHeaderMobile: FC<Props> = ({ folder, isOwner, onShareClick }) => {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  // don't render thumb for anon user on ssr because screen size value might be off
  const { width: screenSize } = useContext(ScreenSizeContext);
  const { isLoggedIn } = useContext(SessionContext);
  const thumb = folder.thumb;
  const showThumb = thumb && !thumb.isDefaultImage && (!isSSR() || isLoggedIn);
  const [expanded, setExpanded] = useState(false);
  const onExpand = useCallback(toggle => {
    setExpanded(toggle);
  }, []);

  return (
    <div className={cn(s['root'], expanded && s['expanded'])}>
      {showThumb && thumb && (
        <div className={s['folder-thumb-wrapper']}>
          <Dumbo className={s['folder-thumb']} deviation={thumb} width={screenSize} height={178} />
        </div>
      )}
      {showThumb && <div className={s['folder-thumb-overlay']} />}

      <div className={s['top']}>
        {isOwner && <StudioBreadcrumbs />}
        {!isOwner && <div className={s['folder-name']}>{folder.name}</div>}

        <BiLoggerContextProvider value={{ sectionname: 'header_actions' }}>
          <div className={s['actions']}>
            {isOwner && activeSection === StudioSection.STASH && (
              <SimpleUploadButton label={t('studio.header.upload')} />
            )}
            {isOwner && <FolderActionsDropdown folder={folder} withBookmark />}
            {!isOwner && (
              <IconButton
                icon={DownloadIcon}
                iconSizeOverride={IconSize.LARGE}
                size="large"
                variant="main"
                aria-label={t('studio.header.download')}
                href={Url.stashFolderZipDownload(folder.folderId)}
                noPaddings
                biData={BiData<DownloadAllClickBiEvent>({
                  evid: 18,
                  typeid: BiContextProvidedValue,
                  itemid: BiContextProvidedValue,
                  view: BiContextProvidedValue,
                  component: BiContextProvidedValue,
                  sectionname: 'folder_header',
                })}
              />
            )}
            {!isOwner && (
              <IconButton
                icon={ShareIcon}
                iconSizeOverride={IconSize.LARGE}
                size="large"
                variant="main"
                aria-label={t('share.copyLink')}
                onClick={onShareClick}
                noPaddings
                biData={BiData<ClickCopyBiEvent>({
                  evid: 357,
                  typeid: BiContextProvidedValue,
                  itemid: BiContextProvidedValue,
                  view: BiContextProvidedValue,
                  component: BiContextProvidedValue,
                  sectionname: 'folder_header',
                })}
              />
            )}
          </div>
        </BiLoggerContextProvider>
      </div>
      <FolderDescription className={s['folder-description']} folder={folder} onExpand={onExpand} />
    </div>
  );
};
FolderHeaderMobile.displayName = 'FolderHeaderMobile';

export default FolderHeaderMobile;
